import { MODULE_POWUR_OS_EQUIPMENT_ID } from "@logic/lib/powur-os"
import type { ModuleInverterCompatibilityData } from "@logic/lib/powur-os"
import { z } from "zod"

import { PricingSummaryLineItemType } from "~/utils/finance/pricing-summary"
import type { Point2d } from "~/utils/geometry"

const dateLikeSchema = z.union([z.date(), z.string()])
const nullableDateLikeSchema = dateLikeSchema.nullable()

export const ROOF_EDGE_TYPES = [
  `eave`,
  `hip`,
  `rake`,
  `ridge`,
  `valley`,
] as const
export type RoofEdgeType = (typeof ROOF_EDGE_TYPES)[number]

export const PHASES = [
  `Image Selection`,
  `Outline`,
  `Edge Designation`,
  `Roof Attributes`,
  `Panel Placement`,
  `Panel Activation`,
] as const
export type Phase = (typeof PHASES)[number]

export const TOOLS = [
  `Select`,
  `Draw`,
  `Pan`,
  `Erase`,
  `PanelCreatorLandscape`,
  `PanelCreatorPortrait`,
  `PanelActivator`,
  `PanelEraser`,
  `GroundMount`,
  `Trees`,
  `EdgeDesignator`,
  `Move`,
  `Ruler`,
] as const
export type Tool = (typeof TOOLS)[number]

export type Setbacks = Record<RoofEdgeType, number>

export const DEFAULT_SETBACKS_IN_INCHES: Setbacks = {
  ridge: 36,
  eave: 0,
  hip: 18,
  rake: 36,
  valley: 18,
}

export const ABANDON_DESIGN_DELAY_MINUTES = 30
export const ABANDON_DESIGN_DELAY_MS = 30 * 60 * 1000

export type RoofEdge = {
  node1id: string
  node2id: string
  edgeType: RoofEdgeType
  setback?: number | null
}

type GroundMaterial = `concrete` | `grass`
type RoofMaterial =
  | `concrete`
  | `glass`
  | `metal`
  | `shingles`
  | `thatch`
  | `wood`
type Substrate = GroundMaterial | RoofMaterial
type MountLocation = `ground` | `roof`

export type InstallArea = {
  material: Substrate
  mountLocation: MountLocation
  azimuth: number | null
  azimuthLocked: boolean
  slope: number
  nodeIds: string[]
  manualShading: number | null
  sunroofShading: number | null
  preferSunroof: boolean
}

export type Panel = {
  x: number
  y: number
  w: number
  h: number
  r: number
  isActive: boolean | null
  xSnap?: number | null
  ySnap?: number | null
  wOriginal?: number
  hOriginal?: number
  orientation?: number | null
  module: string | null
  moduleOsId: number | null
}

export function isPanel(x: unknown): x is Panel {
  return (
    typeof x === `object` &&
    x !== null &&
    typeof (x as Panel).x === `number` &&
    typeof (x as Panel).y === `number` &&
    typeof (x as Panel).w === `number` &&
    typeof (x as Panel).h === `number` &&
    typeof (x as Panel).r === `number` &&
    typeof (x as Panel).isActive === `boolean`
  )
}

type CompatibleManufacturerData = {
  compatibleInverterManufacturerIds: number[]
} | null

type BatteryData = {
  compatibleInverterManufacturerIds: number[]
  hasBackupCapability?: boolean | null
  qty?: number | null
  leadBatteryQty?: number | null
  followerBatteryQty?: number | null
  usableBatteryCapacityKilowatts?: number | null
  roundTripEfficiency?: number | null
  ratedContinuousOutputPowurKilowatts?: number | null
} | null

export type NewEquipment = {
  id: number
  name: string
  proposalName: string | null
  typeId: number
  currentProposalPrice: number
  description: string | null
  manufacturerId: number
  manufacturer: string
  catalogNumber: string | null
  isKit: boolean
  isBattery: boolean
  isDefault: boolean
  isDomesticContent?: boolean
  batteryData: BatteryData
  evChargerData: CompatibleManufacturerData
  moduleData: ModuleInverterCompatibilityData | null
  wattage: number | null
  height: number | null
  width: number | null
  manufacturerNumber?: string | null
  type?: string
}

const compatibleManufactureSchema = z
  .object({
    compatibleInverterManufacturerIds: z.array(z.number().int()),
  })
  .nullable()

const batteryDataSchema = z
  .object({
    compatibleInverterManufacturerIds: z.array(z.number().int()),
    hasBackupCapability: z.boolean().nullable().optional(),
    qty: z.number().int().nonnegative().nullable().optional(),
    leadBatteryQty: z.number().int().nonnegative().nullable().optional(),
    followerBatteryQty: z.number().int().nonnegative().nullable().optional(),
    usableBatteryCapacityKilowatts: z.number().nullable().optional(),
    roundTripEfficiency: z.number().nullable().optional(),
    ratedContinuousOutputPowurKilowatts: z.number().nullable().optional(),
  })
  .nullable()

export const newEquipmentSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  proposalName: z.string().nullable(),
  typeId: z.number().int(),
  currentProposalPrice: z.number(),
  description: z.string().nullable(),
  manufacturerId: z.number().int(),
  manufacturer: z.string(),
  catalogNumber: z.string().nullable(),
  isKit: z.boolean(),
  isBattery: z.boolean(),
  isDefault: z.boolean(),
  isDomesticContent: z.boolean().optional(),
  batteryData: batteryDataSchema,
  evChargerData: compatibleManufactureSchema,
  moduleData: z
    .object({
      incompatibleInverters: z.array(z.number().int()).nullable().optional(),
    })
    .passthrough()
    .nullable()
    .default(null),
  wattage: z.number().nullable(),
  height: z.number().nullable(),
  width: z.number().nullable(),
  manufacturerNumber: z.string().nullable().optional(),
  type: z.string().optional(),
})

export type Equipment = {
  id: number
  name: string
  powurOsEquipmentTypeId: number
  wattage: number | null
  width?: number | null
  height?: number | null
  isBattery?: boolean | null
  isDefault?: boolean | null
  currentProposalPrice: number
  catalogNumber?: string | null
  powurOsManufacturerId?: number | null
  manufacturer?: { name: string; powurOsId?: number | null } | null
  inverterData?: { isBattery?: boolean | null } | null
  moduleData?: ModuleInverterCompatibilityData | null
}

export type DesignEquipment = {
  id: string
  createdAt?: Date | string
  updatedAt?: Date | string
  equipmentId: number
  powurOsEquipmentTypeId: number
  designId: string
  quantity: number
  price: number
  equipment?: Equipment | null
}

export type Design = {
  id: string
  createdAt: Date | string
  updatedAt: Date | string
  roofType: string | null
  activatedAt: Date | string | null
  designEquipment: DesignEquipment[]
  batteryCompatibility: boolean
  brochurePositionX: number
  brochurePositionY: number
  brochureZoom: number
  completedAt: Date | string | null
  annualProduction: number | null
  annualOffset: number | null
  systemSizeKw: number | null
  designRequestId: string | null
  displayImageryProvider: string
  edges: Record<string, RoofEdge>
  energyBackup: Equipment | null
  energyBackupId: number | null
  profiles: unknown[]
  energyEfficiencyKitRebate: boolean
  energyEfficiencyKitRebateAmount: number
  evCharger: boolean
  evOutlet: boolean
  estimates: unknown[]
  optimalEstimate: unknown | null
  homeownerRebate: boolean
  homeownerRebateAmount: number
  installAreas: Record<string, InstallArea>
  installAreasToPanels: Array<[string, string[]]>
  inverter: Equipment | null
  inverterId: number | null
  inverterManufacturer: string | null
  nodes: Record<string, Point2d>
  obstructions: Record<string, { nodeIds: string[] }>
  panels: Record<string, Panel>
  phase: Phase
  poison?: { reason: string; data: unknown } | null
  extendedWarrantyPowurOsId: number | null
  extendedWarrantyName: string | null
  extendedWarrantySpanishName: string | null
  extendedWarrantyFamily: string | null
  extendedWarrantyCost: number | null
  proposalId: string
  roofWorkRebate: boolean
  roofWorkRebateAmount: number
  savings: unknown | null
  secondaryInverter: Equipment | null
  secondaryInverterId: number | null
  source: `aurora` | `vision`
  setbacks: Setbacks
  shadingAreas: Record<string, { nodeIds: string[] }>
  sunroofCheckedAt: Date | string | null
  tool: Tool
  openSolarImage: {
    filename?: string | null
    height?: number | null
    width?: number | null
  } | null
  treeTrimmingRebate: boolean
  treeTrimmingRebateAmount: number
  type: string | null
  parentId: string | null
  name: string | null
}

const panelSchema = z
  .object({
    x: z.number(),
    y: z.number(),
    w: z.number(),
    h: z.number(),
    r: z.number(),
    isActive: z.boolean().nullable().default(null),
    xSnap: z.number().nullable().optional(),
    ySnap: z.number().nullable().optional(),
    wOriginal: z.number().optional(),
    hOriginal: z.number().optional(),
    orientation: z.number().nullable().optional(),
    module: z.string().nullable().default(null),
    moduleOsId: z.number().nullable().default(null),
  })
  .passthrough()

const panelsSchema = z.record(z.unknown()).transform((input) => {
  const panels: Record<string, Panel> = {}
  for (const [key, value] of Object.entries(input)) {
    const result = panelSchema.safeParse(value)
    if (result.success) {
      panels[key] = result.data
    } else {
      console.error(result.error)
    }
  }

  return panels
})

const edgeSchema: z.ZodType<RoofEdge> = z
  .object({
    node1id: z.string(),
    node2id: z.string(),
    edgeType: z.enum(ROOF_EDGE_TYPES),
    setback: z.number().nullable().optional(),
  })
  .passthrough()

const equipmentSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    powurOsEquipmentTypeId: z.number(),
    wattage: z.number().nullable().default(null),
    width: z.number().nullable().optional(),
    height: z.number().nullable().optional(),
    isBattery: z.boolean().nullable().optional(),
    isDefault: z.boolean().nullable().optional(),
    currentProposalPrice: z.number(),
    powurOsManufacturerId: z.number().nullable().optional(),
    manufacturer: z
      .object({
        name: z.string().default(``),
        powurOsId: z.number().nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    inverterData: z
      .object({ isBattery: z.boolean().nullable().optional() })
      .passthrough()
      .nullable()
      .optional(),
    moduleData: z
      .object({
        incompatibleInverters: z.array(z.number().int()).nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough()

const designEquipmentSchema = z
  .object({
    id: z.string(),
    createdAt: dateLikeSchema.optional(),
    updatedAt: dateLikeSchema.optional(),
    equipmentId: z.number(),
    powurOsEquipmentTypeId: z.number(),
    designId: z.string(),
    quantity: z.number(),
    price: z.number(),
    equipment: equipmentSchema.optional().nullable(),
  })
  .passthrough()

const installAreaSchema = z
  .object({
    material: z.enum([
      `concrete`,
      `glass`,
      `metal`,
      `shingles`,
      `thatch`,
      `wood`,
      `grass`,
    ]),
    mountLocation: z.enum([`ground`, `roof`]),
    azimuth: z.number().nullable().default(null),
    azimuthLocked: z.boolean(),
    slope: z.number(),
    nodeIds: z.array(z.string()),
    manualShading: z.nullable(z.number().min(-5).max(100).default(3)),
    sunroofShading: z.nullable(z.number().min(-5).max(100).default(3)),
    preferSunroof: z.boolean().default(true),
  })
  .passthrough()

const openSolarImageSchema = z
  .object({
    filename: z.string().nullable().optional(),
    height: z.number().nullable().optional(),
    width: z.number().nullable().optional(),
  })
  .passthrough()

const designSchemaBase = z
  .object({
    id: z.string().default(``),
    createdAt: dateLikeSchema.default(new Date(0)),
    updatedAt: dateLikeSchema.default(new Date(0)),
    roofType: z.string().nullable().default(null),
    activatedAt: nullableDateLikeSchema.default(null),
    designEquipment: z.array(designEquipmentSchema).optional().default([]),
    batteryCompatibility: z.boolean().default(false),
    brochurePositionX: z.number().default(0),
    brochurePositionY: z.number().default(0),
    brochureZoom: z.number().default(1.1),
    completedAt: nullableDateLikeSchema.default(null),
    annualProduction: z.number().nullable().default(null),
    annualOffset: z.number().nullable().default(null),
    systemSizeKw: z.number().nullable().default(null),
    designRequestId: z.string().nullable().default(null),
    displayImageryProvider: z.string().default(`nearmap`),
    edges: z.record(z.string(), edgeSchema).default({}),
    energyBackup: equipmentSchema.nullable().default(null),
    energyBackupId: z.number().nullable().default(null),
    profiles: z.array(z.unknown()).optional().default([]),
    energyEfficiencyKitRebate: z.boolean().default(false),
    energyEfficiencyKitRebateAmount: z.number().default(0),
    evCharger: z.boolean().default(false),
    evOutlet: z.boolean().default(false),
    estimates: z.array(z.unknown()).optional().default([]),
    optimalEstimate: z.unknown().nullable().default(null),
    homeownerRebate: z.boolean().default(false),
    homeownerRebateAmount: z.number().default(0),
    installAreas: z.record(z.string(), installAreaSchema).default({}),
    installAreasToPanels: z
      .array(z.tuple([z.string(), z.array(z.string())]))
      .default([]),
    inverter: equipmentSchema.nullable().default(null),
    inverterId: z.number().nullable().default(null),
    inverterManufacturer: z.string().nullable().default(null),
    nodes: z
      .record(z.string(), z.object({ x: z.number(), y: z.number() }))
      .default({}),
    obstructions: z
      .record(z.string(), z.object({ nodeIds: z.array(z.string()) }))
      .default({}),
    panels: panelsSchema.default({}),
    phase: z.enum(PHASES).catch(`Outline`).default(`Image Selection`),
    poison: z
      .object({ reason: z.string(), data: z.unknown() })
      .optional()
      .nullable()
      .default(null),
    extendedWarrantyPowurOsId: z.number().nullable().default(null),
    extendedWarrantyName: z.string().nullable().default(null),
    extendedWarrantySpanishName: z.string().nullable().default(null),
    extendedWarrantyFamily: z.string().nullable().default(null),
    extendedWarrantyCost: z.number().nullable().default(null),
    proposalId: z.string().default(``),
    roofWorkRebate: z.boolean().default(false),
    roofWorkRebateAmount: z.number().default(0),
    savings: z.unknown().nullable().default(null),
    secondaryInverter: equipmentSchema.optional().nullable().default(null),
    secondaryInverterId: z.number().nullable().default(null),
    source: z.enum([`aurora`, `vision`]).default(`vision`),
    setbacks: z
      .object({
        eave: z.number(),
        hip: z.number(),
        rake: z.number(),
        ridge: z.number(),
        valley: z.number(),
      })
      .default(DEFAULT_SETBACKS_IN_INCHES),
    shadingAreas: z
      .record(z.string(), z.object({ nodeIds: z.array(z.string()) }))
      .default({}),
    sunroofCheckedAt: nullableDateLikeSchema.default(null),
    tool: z.enum(TOOLS).catch(`Select`).default(`Pan`),
    openSolarImage: openSolarImageSchema.nullable().default(null),
    treeTrimmingRebate: z.boolean().default(false),
    treeTrimmingRebateAmount: z.number().default(0),
    type: z.string().nullable().default(null),
    parentId: z.string().nullable().default(null),
    name: z.string().optional().nullable().default(null),
  })
  .passthrough() as unknown as z.ZodType<Design>

const designTransformer = (design: Design): Design => {
  const safePanelIds = Object.keys(design.panels)
  const safeInstallAreaIds = Object.keys(design.installAreas)
  design.installAreasToPanels = design.installAreasToPanels
    .filter(([installAreaId]) => safeInstallAreaIds.includes(installAreaId))
    .map(([installAreaId, panelIds]) => [
      installAreaId,
      panelIds.filter((id) => safePanelIds.includes(id)),
    ])

  return design
}

export const designSchema = designSchemaBase.transform(designTransformer)

export const DEFAULT_DESIGN: Design = {
  id: ``,
  createdAt: new Date(),
  updatedAt: new Date(),
  roofType: null,
  activatedAt: null,
  designEquipment: [],
  batteryCompatibility: false,
  brochurePositionX: 0,
  brochurePositionY: 0,
  brochureZoom: 1.1,
  completedAt: null,
  annualProduction: null,
  annualOffset: null,
  systemSizeKw: null,
  displayImageryProvider: `nearmap`,
  edges: {},
  energyBackup: null,
  energyBackupId: null,
  profiles: [],
  energyEfficiencyKitRebate: false,
  energyEfficiencyKitRebateAmount: 0,
  evCharger: false,
  evOutlet: false,
  estimates: [],
  optimalEstimate: null,
  homeownerRebate: false,
  homeownerRebateAmount: 0,
  installAreas: {},
  installAreasToPanels: [],
  inverter: null,
  inverterId: null,
  inverterManufacturer: null,
  nodes: {},
  obstructions: {},
  panels: {},
  phase: `Image Selection`,
  poison: null,
  extendedWarrantyPowurOsId: null,
  extendedWarrantyName: null,
  extendedWarrantySpanishName: null,
  extendedWarrantyFamily: null,
  extendedWarrantyCost: null,
  proposalId: ``,
  roofWorkRebate: false,
  roofWorkRebateAmount: 0,
  savings: null,
  secondaryInverter: null,
  secondaryInverterId: null,
  source: `vision`,
  setbacks: DEFAULT_SETBACKS_IN_INCHES,
  shadingAreas: {},
  sunroofCheckedAt: null,
  tool: `Pan`,
  openSolarImage: null,
  treeTrimmingRebate: false,
  treeTrimmingRebateAmount: 0,
  type: `clay_tile`,
  parentId: null,
  designRequestId: null,
  name: ``,
}

export const getSolarModuleFromDesignEquipment = <
  T extends {
    powurOsEquipmentTypeId?: number | null
    equipment?: { powurOsEquipmentTypeId?: number | null } | null
  },
>(
  designEquipment: T[] | null | undefined,
): T | null =>
  designEquipment?.find(
    (item) => item.powurOsEquipmentTypeId === MODULE_POWUR_OS_EQUIPMENT_ID,
  ) ?? null

type SystemSizeParams = {
  panels: Record<string, { isActive: boolean | null }>
  installAreasToPanels: Array<[string, string[]]>
  solarModule: { wattage?: number | null } | null
}

export const getActivePanelsEntries = <
  P extends { isActive: boolean | null },
>(design: {
  panels: Record<string, P>
  installAreasToPanels: Array<[string, string[]]>
}): [string, P][] =>
  Object.entries(design.panels).filter(
    ([panelId, panel]) =>
      panel.isActive &&
      design.installAreasToPanels.some(([, panelIds]) =>
        panelIds.includes(panelId),
      ),
  )

export const getSystemSizeInWatts = (design: SystemSizeParams): number => {
  const { solarModule } = design
  if (!solarModule || typeof solarModule.wattage !== `number`) {
    return 0
  }

  return solarModule.wattage * getActivePanelsEntries(design).length
}

export function getDesignCostItems(design: {
  panels: Record<string, { isActive: boolean | null }>
  installAreasToPanels: Array<[string, string[]]>
  solarModule: { wattage?: number | null } | null
}): {
  systemSizeInWatts: number
  arrays: [string, string[]][]
  panels: [string, Panel][]
} {
  const installAreasWithPanels =
    design.installAreasToPanels?.filter(
      ([, p]) =>
        p.length > 0 && p.some((panelId) => design.panels?.[panelId]?.isActive),
    ) ?? []

  const activePanelsEntries = Object.entries(design.panels ?? {}).filter(
    ([, p]) => p.isActive,
  )
  const systemSizeInWatts = getSystemSizeInWatts(design)

  return {
    systemSizeInWatts,
    arrays: installAreasWithPanels,
    panels: activePanelsEntries as [string, Panel][],
  }
}

export { PricingSummaryLineItemType }
